import { useCallback } from 'react';

import { AllTaskValues } from './types';

export type ShellState = {
  currentIndex: number;
  editingFromConfirmation: boolean;
  values: AllTaskValues;
  visited: Set<number>;
};

type PersistedShellState = {
  currentIndex: number;
  editingFromConfirmation: boolean;
  values: AllTaskValues;
  visited: number[];
};

// File instances (Insurance attachments) can't survive a page reload, so strip
// them from the persisted payload. Everything else round-trips through JSON.
const stripFiles = (_key: string, value: unknown): unknown => {
  if (typeof File !== 'undefined' && value instanceof File) {
    return undefined;
  }
  if (Array.isArray(value)) {
    return value.filter((item) => !(typeof File !== 'undefined' && item instanceof File));
  }
  return value;
};

export const useDraftStorage = (storageKey: string | undefined, taskCount: number) => {
  const readDraft = useCallback((): ShellState | null => {
    if (!storageKey || typeof window === 'undefined') return null;
    try {
      const raw = window.sessionStorage.getItem(storageKey);
      if (!raw) return null;
      const draft = JSON.parse(raw) as PersistedShellState;
      // Clamp index in case the task list shrank since the draft was saved.
      const currentIndex = Math.min(Math.max(draft.currentIndex ?? 0, 0), taskCount);
      return {
        currentIndex,
        editingFromConfirmation: !!draft.editingFromConfirmation,
        values: draft.values ?? {},
        visited: new Set<number>([0, ...(draft.visited ?? []).filter((i) => i >= 0 && i <= taskCount)]),
      };
    } catch {
      return null;
    }
  }, [storageKey, taskCount]);

  const persistDraft = useCallback(
    (state: ShellState): void => {
      if (!storageKey || typeof window === 'undefined') return;
      try {
        const payload: PersistedShellState = {
          currentIndex: state.currentIndex,
          editingFromConfirmation: state.editingFromConfirmation,
          values: state.values,
          visited: Array.from(state.visited),
        };
        window.sessionStorage.setItem(storageKey, JSON.stringify(payload, stripFiles));
      } catch {
        // Ignore quota/serialization failures — in-memory state remains source of truth.
      }
    },
    [storageKey],
  );

  const clearDraft = useCallback((): void => {
    if (!storageKey || typeof window === 'undefined') return;
    try {
      window.sessionStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
  }, [storageKey]);

  return { readDraft, persistDraft, clearDraft };
};
